import { Injectable } from '@angular/core';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {UserPreferencesPortfolioService} from '../user-preferences-portfolio/user-preferences-portfolio.service';
import {NavigationDataModel} from '../../../models/navigation-data.model';
import {LocalStorageService} from '../../local-storage-service/local-storage.service';
import { ParkTurbineNavigation } from 'app/models/park-turbine-navigation/park-turbine-navigation.model';
import { MenusDropDownGQL, ParkTurbineListGQL, ParkTurbineMenuGQL} from '../graphql';
import { PageNavigationItem } from './navigation.interface';
import { ActivatedRouteSnapshot } from '@angular/router';
import { DeviceGroupsService } from 'app/shared/services-api-v2/device-groups/device-groups.service';
@Injectable({
  providedIn: 'root'
})
export class NavigationDataService {
  private portfoliosList: BehaviorSubject<NavigationDataModel> = new BehaviorSubject<NavigationDataModel>(null);
  private viewItemsList: BehaviorSubject<PageNavigationItem[]> = new BehaviorSubject<PageNavigationItem[]>([]);
  private routeSnapshot: BehaviorSubject<ActivatedRouteSnapshot> = new BehaviorSubject<ActivatedRouteSnapshot>(null);

  constructor(
    private userPreferencesPortfolioService: UserPreferencesPortfolioService,
    private localStorageService: LocalStorageService,
    private deviceGroupService: DeviceGroupsService,
    private parkTurbineMenuGQL: ParkTurbineMenuGQL) {
      this.deviceGroupService.initDeviceGroupsData();
    }

  loadNavigationData(): Observable<any> {
    const portfolioData$ = this.userPreferencesPortfolioService.getUserPortfoliosWithParksAndTurbines();
    const deviceGroup$ =  this.deviceGroupService.getGroupsData();

    return combineLatest([portfolioData$, deviceGroup$]).pipe(map(([result, deviceGroup]) => {
      const allData = NavigationDataModel.mapData(result, this.localStorageService.getSelectedPortfolioId(),deviceGroup)
      this.portfoliosList.next(allData);
      return allData;
    }));
  }

  updateNavigationData(id): Observable<any> {
    return this.userPreferencesPortfolioService.getUserPortfolioService()
        .pipe(map(result => {
      const allData = NavigationDataModel.mapData({data: {user: result}}, id,this.deviceGroupService.getGroupsDataSync())
      this.portfoliosList.next(allData);
      return allData;
    }));
  }

  updateDeviceGroupsInNavigationData(){
    const currentData = this.portfoliosList.value;
    currentData.powerplants.forEach(park => {
      const groups = this.deviceGroupService.getGroupsDataSync().filter(group => group._id_park === park.id)?.flatMap(park => park.turbine_groups);
      park.groups = groups;
    })
    this.portfoliosList.next(currentData);
  }

  public getSubPortfolioListData(): BehaviorSubject<NavigationDataModel> {
    return this.portfoliosList;
  }

  public getParkNameForNavigationById(parkId: string) {
    if (!parkId) {
      return;
    }
    const data = this.getSubPortfolioListData();
    if (!!data && !!data.value) {
      const powerPlant = data.value.powerplants.find(p => p.id === parkId);
      return powerPlant?.nameLong;
    }
    return '---';
  }

  public getTurbineById(turbineId: string) {
    if (!turbineId) {
      return;
    }
    const data = this.getSubPortfolioListData();
    if (!!data && !!data.value) {
      let result = null;
      data.value.powerplants.map((p) => {
        const foundTurbine = p.turbines.find(t => t.id === turbineId);
        if (foundTurbine) {
          result = foundTurbine;
        }
      })
      return result;
    }
  }  

  public getParkCountryById(parkId: string) {
    if (!parkId) {
      return;
    }
    const data = this.getSubPortfolioListData();
    if (!!data && !!data.value) {
      const powerPlant = data.value.powerplants.find(p => p.id === parkId);
      return powerPlant.countryCode;
    }
  }

  public getTurbineSerialForNavigationById(turbineId: string) {
    if (!turbineId) {
      return;
    }
    let result = null;
    const data = this.getSubPortfolioListData();
    if (!!data && !!data.value) {
      data.value.powerplants.map((p) => {
        const foundTurbine = p.turbines.find(t => t.id === turbineId);
        if (foundTurbine) {
          result = foundTurbine.serialNav;
        }
      })
    }
    return result;
  }

  public getParkTurbineMenu(menuTypeObservable: Observable<string>): Observable<any> {
    return menuTypeObservable.pipe(
      distinctUntilChanged(),
      switchMap((menuType: string) =>
        this.parkTurbineMenuGQL.subscribe().pipe(
          map(result => ParkTurbineNavigation.mapParkTurbineNavigation(result.data.countries, menuType))
        )
      )
    );
  }

  public getParksList() {

    const data = this.getSubPortfolioListData();
    if (!!data && !!data.value) {
      return data.value.powerplants;
    }
  }

  public getViewItemsList(): BehaviorSubject<PageNavigationItem[]> {
    return this.viewItemsList;
  }

  public setViewItemsList(items: PageNavigationItem[]) {
    this.viewItemsList.next(items);
  }

  public getRouteSnapshot(): BehaviorSubject<ActivatedRouteSnapshot> {
    return this.routeSnapshot;
  }

  public setRouteSnapshot(snapshot: ActivatedRouteSnapshot) {
    this.routeSnapshot.next(snapshot);
  }
}
 
